import moment from "moment";

const WEEK_DAYS = [
  "Saturday",
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const needsForm = (type, code, message, details = {}) => ({
  outcome: "needs_form",
  type,
  reason: {
    code,
    message,
    ...details,
  },
});

const ready = (type, endpoint, payload) => ({
  outcome: "ready",
  type,
  endpoint,
  method: "post",
  payload,
});

const missingFields = (fields, keys) =>
  keys.filter((key) => !hasValue(fields[key]));

const missingRequired = (type, fields, keys) => {
  const missing = missingFields(fields, keys);
  return missing.length
    ? needsForm(
        type,
        "missing_required_fields",
        `Missing ${missing.join(", ")}.`,
        { fields: missing }
      )
    : null;
};

const selectedId = (selection) => {
  if (!selection || typeof selection !== "object") return selection;
  return hasValue(selection.value) ? selection.value : selection.id;
};

const validLookupId = (id) =>
  (typeof id === "string" && id.trim() !== "") ||
  (typeof id === "number" && Number.isFinite(id));

const validPhone = (value) => {
  // PhoneInput stores accepted values in E.164 form and clears invalid input.
  return typeof value === "string" && /^\+[1-9]\d{7,14}$/.test(value);
};

export const prepareLeadPayload = (fields = {}) => {
  const invalid = missingRequired("lead", fields, [
    "firstName",
    "lastName",
    "assignedStaff",
    "referralSource",
  ]);
  if (invalid) return invalid;

  if (!hasValue(fields.email) && !hasValue(fields.phone)) {
    return needsForm(
      "lead",
      "missing_contact",
      "The manual lead form requires an email address or phone number.",
      { fields: ["email", "phone"] }
    );
  }

  const assignedTo = selectedId(fields.assignedStaff);
  const referralSource = selectedId(fields.referralSource);
  const invalidLookups = [];
  if (!validLookupId(assignedTo)) invalidLookups.push("assignedStaff");
  if (!validLookupId(referralSource)) invalidLookups.push("referralSource");
  if (invalidLookups.length) {
    return needsForm(
      "lead",
      "missing_lookup_ids",
      "Lead creation requires the selected staff and referral-source IDs.",
      { fields: invalidLookups }
    );
  }

  return ready("lead", "lead", {
    firstName: fields.firstName,
    lastName: fields.lastName,
    email: fields.email,
    phone: fields.phone,
    gender: fields.gender,
    dob: fields.dob,
    priority: hasValue(fields.priority) ? fields.priority : "medium",
    assignedTo,
    referralSource,
    note: hasValue(fields.note) ? fields.note : "",
  });
};

const productDefaults = {
  image: "",
  type: "",
  barcode: "",
  weight: "",
  productType: "S",
  weightUnit: "",
  note: "",
  purchasedPrice: 0,
};

export const prepareProductPayload = (fields = {}, context = {}) => {
  const values = { ...productDefaults, ...fields };
  const invalid = missingRequired("product", values, [
    "name",
    "expiryDate",
    "price",
    "tax",
    "category",
    "stock",
    "supplier",
    "measureOfPurchase",
    "measureOfUnit",
    "minInStock",
  ]);
  if (invalid) return invalid;

  if (!Array.isArray(context.categories)) {
    return needsForm(
      "product",
      "missing_category_context",
      "Product creation requires the gym category list used by the manual form.",
      { context: ["categories"] }
    );
  }

  const expiryDate = moment(values.expiryDate);
  if (!expiryDate.isValid()) {
    return needsForm(
      "product",
      "invalid_expiry_date",
      "The product expiry date is not a valid date.",
      { fields: ["expiryDate"] }
    );
  }

  const numericFields = ["price", "tax", "stock", "purchasedPrice"];
  const invalidNumbers = numericFields.filter(
    (key) => !Number.isFinite(Number(values[key]))
  );
  if (invalidNumbers.length) {
    return needsForm(
      "product",
      "invalid_numeric_fields",
      "Product prices, tax, stock, and purchase price must be numeric.",
      { fields: invalidNumbers }
    );
  }

  let payrateId = null;
  if (hasValue(values.payRates)) {
    if (!Array.isArray(values.payRates)) {
      return needsForm(
        "product",
        "invalid_pay_rates",
        "Product pay rates must retain the manual form's array shape.",
        { fields: ["payRates"] }
      );
    }
    if (values.payRates.length) {
      payrateId = selectedId(values.payRates[0]);
      if (!validLookupId(payrateId)) {
        return needsForm(
          "product",
          "missing_payrate_id",
          "The selected product pay rate is missing its ID.",
          { fields: ["payRates"] }
        );
      }
    }
  }

  return ready("product", "product", {
    ...values,
    payrateId,
    cost: values.stock * values.purchasedPrice,
    purchasedPrice: values.purchasedPrice,
    netPrice: values.price,
    expiryDate: expiryDate.valueOf(),
    existingCategories: context.categories,
  });
};

const emptySchedule = () =>
  WEEK_DAYS.reduce((schedule, day) => {
    schedule[day] = [];
    return schedule;
  }, {});

const prepareSchedule = (scheduleDays) => {
  if (
    hasValue(scheduleDays) &&
    (typeof scheduleDays !== "object" ||
      Array.isArray(scheduleDays) ||
      scheduleDays === null)
  ) {
    return { error: "invalid_schedule" };
  }

  const schedule = emptySchedule();
  for (const day of WEEK_DAYS) {
    if (scheduleDays && hasValue(scheduleDays[day])) {
      if (!Array.isArray(scheduleDays[day])) {
        return { error: "invalid_schedule", day };
      }
      schedule[day] = scheduleDays[day].map((period) => ({ ...period }));
      if (
        schedule[day].some(
          (period) =>
            !period ||
            typeof period !== "object" ||
            !hasValue(period.start) ||
            !hasValue(period.end)
        )
      ) {
        return { error: "incomplete_schedule_period", day };
      }
    }
  }

  if (WEEK_DAYS.every((day) => schedule[day].length === 0)) {
    for (const day of WEEK_DAYS) {
      schedule[day] = [{ start: "05:00", end: "23:00" }];
    }
  }
  return { schedule };
};

export const prepareInstructorPayload = (fields = {}, context = {}) => {
  const values = {
    image: "",
    activeStatus: "ACTIVE",
    payRates: [],
    qualifications: [],
    canLive: true,
    trainerAttributes: [],
    addTrainerAsMember: false,
    ...fields,
  };
  const invalid = missingRequired("instructor", values, [
    "name",
    "email",
    "phone",
    "gender",
    "activeStatus",
  ]);
  if (invalid) return invalid;

  if (!validPhone(values.phone)) {
    return needsForm(
      "instructor",
      "invalid_phone",
      "The instructor phone number must be valid before the manual form retains it.",
      { fields: ["phone"] }
    );
  }

  if (!validLookupId(context.gym)) {
    return needsForm(
      "instructor",
      "missing_gym_id",
      "Instructor creation requires the current gym ID.",
      { context: ["gym"] }
    );
  }

  if (!Array.isArray(values.qualifications) || values.qualifications.length > 8) {
    return needsForm(
      "instructor",
      "invalid_qualifications",
      "Instructor qualifications must be an array containing at most eight values.",
      { fields: ["qualifications"] }
    );
  }
  if (!Array.isArray(values.payRates)) {
    return needsForm(
      "instructor",
      "invalid_pay_rates",
      "Instructor pay rates must retain the manual form's array shape.",
      { fields: ["payRates"] }
    );
  }
  if (!Array.isArray(values.trainerAttributes)) {
    return needsForm(
      "instructor",
      "invalid_trainer_attributes",
      "Instructor attributes must retain the manual form's array shape.",
      { fields: ["trainerAttributes"] }
    );
  }
  if (values.addTrainerAsMember) {
    return needsForm(
      "instructor",
      "member_conversion_required",
      "Adding an instructor as a member requires the manual two-request flow.",
      { fields: ["addTrainerAsMember"] }
    );
  }

  const preparedSchedule = prepareSchedule(values.scheduleDays);
  if (preparedSchedule.error) {
    return needsForm(
      "instructor",
      preparedSchedule.error,
      "Every instructor schedule period requires a start and end time.",
      {
        fields: ["scheduleDays"],
        ...(preparedSchedule.day ? { day: preparedSchedule.day } : {}),
      }
    );
  }

  return ready("instructor", "trainer", {
    name: values.name,
    email: values.email,
    phone: values.phone,
    image: values.image,
    activeStatus: values.activeStatus,
    payRates: values.payRates,
    qualities: values.qualifications.toString(),
    canLive: values.canLive === "true" ? true : Boolean(values.canLive),
    trainerAttributes: values.trainerAttributes,
    gender: values.gender,
    dob: values.dob,
    scheduleDays: preparedSchedule.schedule,
    role: "TRAINER",
    gym: context.gym,
    addTrainerAsMember: false,
  });
};

export const simpleCommands = {
  lead: { preparePayload: prepareLeadPayload },
  product: { preparePayload: prepareProductPayload },
  instructor: { preparePayload: prepareInstructorPayload },
};

export const getSimpleCommand = (type) => simpleCommands[type];

export default simpleCommands;
